import type { Editor } from "@tiptap/react";
import type { Node } from "@tiptap/pm/model";
import type { Transaction } from "@tiptap/pm/state";
import {
  addColumnAfter,
  addColumnBefore,
  addRowAfter,
  addRowBefore,
  columnIsHeader,
  deleteColumn,
  deleteRow,
  deleteTable,
  moveTableColumn,
  moveTableRow,
  rowIsHeader,
  selectedRect,
  toggleHeader,
  type TableMap,
} from "@tiptap/pm/tables";
import {
  cellsOverlapRectangle,
  getIndexCoordinates,
  getRowOrColumnCells,
  getTable,
  isCellEmpty,
  selectCellsByCoords,
  updateSelectionAfterAction,
  type Orientation,
} from "./table-utils";

// Row/column actions ported from the mnemos table kit hooks, flattened into
// plain functions: every action builds a CellSelection for the target
// row/column first, then runs the prosemirror-tables command against it.

export type MoveDirection = "down" | "left" | "right" | "up";

type ActionContext = { editor: Editor; index: number; orientation: Orientation; tablePos: number };

function dispatcher(editor: Editor) {
  return (tr: Transaction) => editor.view.dispatch(tr);
}

function stateWithLineSelected({ editor, index, orientation, tablePos }: ActionContext) {
  const coords = getIndexCoordinates(editor, index, orientation, tablePos);
  return coords ? selectCellsByCoords(editor, tablePos, coords) : null;
}

// Highlight the whole row/column (used when its menu opens).
export function selectRowOrColumn(context: ActionContext): void {
  const coords = getIndexCoordinates(context.editor, context.index, context.orientation, context.tablePos);
  if (coords) selectCellsByCoords(context.editor, context.tablePos, coords, dispatcher(context.editor));
}

export function addRowColumn(context: ActionContext, side: "after" | "before"): boolean {
  const { editor, index, orientation, tablePos } = context;
  const cellState = selectCellsByCoords(
    editor,
    tablePos,
    [orientation === "row" ? { col: 0, row: index } : { col: index, row: 0 }],
  );
  if (!cellState) return false;

  const command =
    orientation === "row" ? (side === "before" ? addRowBefore : addRowAfter) : side === "before" ? addColumnBefore : addColumnAfter;
  const success = command(cellState, dispatcher(editor));
  if (success) {
    updateSelectionAfterAction(editor, orientation, side === "before" ? index : index + 1, tablePos);
  }
  return success;
}

export function deleteRowColumn(context: ActionContext): boolean {
  const { editor, orientation } = context;
  const cellState = stateWithLineSelected(context);
  if (!cellState) return false;
  return (orientation === "row" ? deleteRow : deleteColumn)(cellState, dispatcher(editor));
}

export function removeTable(context: ActionContext): boolean {
  const cellState = stateWithLineSelected(context);
  if (!cellState) return false;
  return deleteTable(cellState, dispatcher(context.editor));
}

export function canMoveRowColumn(context: ActionContext, direction: MoveDirection): boolean {
  const { editor, index, orientation, tablePos } = context;
  try {
    const table = getTable(editor, tablePos);
    if (!table) return false;

    // Header rows/columns stay put — matches the mnemos behaviour.
    if (orientation === "row" && safeIsHeader("row", table.map, table.node, index)) return false;
    if (orientation === "column" && safeIsHeader("column", table.map, table.node, index)) return false;

    const delta = direction === "up" || direction === "left" ? -1 : 1;
    const targetIndex = index + delta;
    const maxIndex = orientation === "row" ? table.map.height : table.map.width;
    if (targetIndex < 0 || targetIndex >= maxIndex) return false;

    const sourceState = stateWithLineSelected(context);
    const targetState = stateWithLineSelected({ ...context, index: targetIndex });
    if (!sourceState || !targetState) return false;

    if (cellsOverlapRectangle(table.map, selectedRect(sourceState)) && cellsOverlapRectangle(table.map, selectedRect(targetState))) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function moveRowColumn(context: ActionContext, direction: MoveDirection): boolean {
  const { editor, index, orientation, tablePos } = context;
  if (!canMoveRowColumn(context, direction)) return false;

  const table = getTable(editor, tablePos);
  if (!table) return false;

  const to = index + (direction === "up" || direction === "left" ? -1 : 1);
  const sourceState = stateWithLineSelected(context);
  if (!sourceState) return false;

  const move = orientation === "row" ? moveTableRow : moveTableColumn;
  return move({ from: index, pos: table.start, select: true, to })(sourceState, dispatcher(editor));
}

export function canDuplicateRowColumn(context: ActionContext): boolean {
  const cellData = getRowOrColumnCells(context.editor, context.index, context.orientation, context.tablePos);
  return cellData.mergedCells.length === 0 && cellData.cells.length > 0;
}

// addRowAfter/addColumnAfter, then copy the original cells into the fresh line.
export function duplicateRowColumn(context: ActionContext): boolean {
  const { editor, index, orientation, tablePos } = context;
  if (!canDuplicateRowColumn(context)) return false;

  try {
    const originalCells = getRowOrColumnCells(editor, index, orientation, tablePos);
    if (originalCells.cells.length === 0) return false;

    const cellState = stateWithLineSelected(context);
    if (!cellState) return false;
    const added = (orientation === "row" ? addRowAfter : addColumnAfter)(cellState, dispatcher(editor));
    if (!added) return false;

    const newCells = getRowOrColumnCells(editor, index + 1, orientation, tablePos);
    if (newCells.cells.length === 0) return false;

    const { state, view } = editor;
    const tr = state.tr;
    // Reverse order keeps positions valid while replacing.
    const cellsToReplace = [...newCells.cells].reverse();
    const sources = [...originalCells.cells].reverse();

    cellsToReplace.forEach((newCell, reverseIndex) => {
      const originalCell = sources[reverseIndex];
      if (newCell.node && originalCell?.node) {
        const duplicated = newCell.node.type.create({ ...originalCell.node.attrs }, originalCell.node.content, originalCell.node.marks);
        tr.replaceWith(newCell.pos, newCell.pos + newCell.node.nodeSize, duplicated);
      }
    });

    if (!tr.docChanged) return false;
    view.dispatch(tr);
    updateSelectionAfterAction(editor, orientation, index + 1, tablePos);
    return true;
  } catch {
    return false;
  }
}

export function clearRowColumnContent(context: ActionContext): boolean {
  const { editor, index, orientation, tablePos } = context;
  try {
    const cellData = getRowOrColumnCells(editor, index, orientation, tablePos);
    if (cellData.cells.length === 0) return false;

    const tr = editor.state.tr;
    [...cellData.cells].reverse().forEach((cellInfo) => {
      if (cellInfo.node && !isCellEmpty(cellInfo.node)) {
        const from = cellInfo.pos + 1;
        const to = cellInfo.pos + cellInfo.node.nodeSize - 1;
        if (from < to) tr.delete(from, to);
      }
    });

    if (!tr.docChanged) return false;
    editor.view.dispatch(tr);
    return true;
  } catch {
    return false;
  }
}

function safeIsHeader(orientation: Orientation, map: TableMap, node: Node, index: number): boolean {
  try {
    return orientation === "row" ? rowIsHeader(map, node, index) : columnIsHeader(map, node, index);
  } catch {
    return false;
  }
}

export type SortDirection = "asc" | "desc";

export function canSortColumn(context: ActionContext): boolean {
  const cellData = getRowOrColumnCells(context.editor, context.index, "column", context.tablePos);
  return cellData.mergedCells.length === 0 && cellData.cells.filter((cell) => cell.node?.type.name !== "tableHeader").length >= 2;
}

// Sorts the values of one column (headers keep their position, empty cells
// sink to the end) — ported from the mnemos table sort.
export function sortColumn(context: ActionContext, direction: SortDirection): boolean {
  const { editor, index, tablePos } = context;
  if (!canSortColumn(context)) return false;

  try {
    const cellData = getRowOrColumnCells(editor, index, "column", tablePos);
    const items = cellData.cells.map((cellInfo) => ({
      cellInfo,
      isEmpty: cellInfo.node ? isCellEmpty(cellInfo.node) : true,
      isHeader: cellInfo.node?.type.name === "tableHeader",
      node: cellInfo.node,
      sortText: cellInfo.node?.textContent.trim() ?? "",
    }));

    const dataItems = items.filter((item) => !item.isHeader);
    dataItems.sort((a, b) => {
      if (a.isEmpty && !b.isEmpty) return 1;
      if (!a.isEmpty && b.isEmpty) return -1;
      if (a.isEmpty && b.isEmpty) return 0;
      const comparison = a.sortText.localeCompare(b.sortText, undefined, { numeric: true, sensitivity: "base" });
      return direction === "asc" ? comparison : -comparison;
    });

    let dataIndex = 0;
    const placedNodes = items.map((item) => (item.isHeader ? item.node : dataItems[dataIndex++]?.node ?? item.node));

    const tr = editor.state.tr;
    // Reverse order keeps positions valid while replacing.
    for (let i = items.length - 1; i >= 0; i -= 1) {
      const targetCell = items[i]!.cellInfo;
      const source = placedNodes[i];
      if (!targetCell.node || !source) continue;
      const replacement = targetCell.node.type.create(source.attrs, source.content, source.marks);
      tr.replaceWith(targetCell.pos, targetCell.pos + targetCell.node.nodeSize, replacement);
    }

    if (!tr.docChanged) return false;
    editor.view.dispatch(tr);
    return true;
  } catch {
    return false;
  }
}

export function isHeaderRowColumn(context: ActionContext): boolean {
  const table = getTable(context.editor, context.tablePos);
  if (!table) return false;
  return safeIsHeader(context.orientation, table.map, table.node, context.index);
}

export function toggleHeaderRowColumn(context: ActionContext): boolean {
  const cellState = stateWithLineSelected(context);
  if (!cellState) return false;
  return toggleHeader(context.orientation === "row" ? "row" : "column")(cellState, dispatcher(context.editor));
}
